import _ from 'lodash'
import { EnvironmentDetail, Exception } from '@amzn/ihm-associate-tools-js-lib'
import { cordovaExec } from '@services/CandyCaneService'
import { EnvironmentPluginConstants } from '@utils/Constants/EnvironmentPluginConstants'
import { store } from '@atredux/store'
import { setTokenInfo, setIsLoggedIn } from '@atredux/slices/loginTokenInfoSlice'
import {
    getAccessToken,
    getIdToken,
    getTokenExpiry,
    getRefreshToken,
    getTokenType,
    getRefreshTokenCallbackTime,
} from '@atredux/selectors/loginTokenInfoSelector'
import { StoreIdService } from '@src/pages/StoreIdSelection/utils/StoreIdService'
import { IUserData } from '@utils/UserData'

export class IIBTokenProvider {

    public static readonly IIB_TOKEN_MEMBERS: any = {
        FULL: 'full',
        ACCESS_TOKEN: 'accessToken',
        ID_TOKEN: 'idToken',
        REFRESH_TOKEN: 'refreshToken',
        TOKEN_TYPE: 'tokenType',
        TOKEN_EXPIRY: 'tokenExpiry',
        REFRESH_TOKEN_CALLBACK_TIME: 'refreshTokenCallbackTime',
    }
    private static readonly IIB_TOKEN_DATA_KEY: string = 'IIBTokenData'
    private static readonly IIB_TOKEN_SCOPES_STORAGE_KEY: string = 'IIBTokenScopes'
    private static readonly IIB_LAST_LOGGEDIN_USER_ID: string = 'IIBLastLoggedInUser'
    // This is to enable IIB based authentication for automation testing in pipelines
    private static readonly ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE = ['test', 'gamma']
    /**
     * Enable Grocery automation Micro-Fulfillment Centers (MFCs)
     * https://w.amazon.com/bin/view/F3/F3BNE/F3Groot/MFCAutomationBackground
     */
    private static readonly ALLOWED_STORES_FOR_LOCALSTORAGE = ['WFM-TEST-RSQ', 'US-FL-18', 'US-AL-2', 'US-PA-9']

    public static async clear () {
        console.log('ATBase: Clearing IIB Token')
        store.dispatch(setTokenInfo({
            _accessToken: null,
            _refreshToken: null,
            _idToken: null,
            _tokenType: null,
            _tokenExpiry: null,
            _refreshTokenCallbackTime: null,
        }))
        if (IIBTokenProvider.isLocalStorageTokenAllowed()) {
            window.localStorage.removeItem(IIBTokenProvider.IIB_TOKEN_DATA_KEY)
            window.localStorage.removeItem(IIBTokenProvider.IIB_TOKEN_SCOPES_STORAGE_KEY)
            window.localStorage.removeItem('ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE')
        } else {
            console.log('ATBase: Executing cordova call to clear the token in shared preferences')
            const isCordovaAvailable = (window as any).cordova && _.isFunction((window as any).cordova.exec)
            if (isCordovaAvailable) {
                await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                    EnvironmentPluginConstants.ACTION_SET_SHARED_PREFERENCES,
                    [IIBTokenProvider.constructJson(IIBTokenProvider.IIB_TOKEN_DATA_KEY, {})])
            }
        }
    }

    public static async setToken (accessToken, refreshToken, idToken, tokenType, tokenExpiry) {
        console.log('ATBase: Setting IIB Token')
        if (_.isEmpty(idToken) || _.isEmpty(tokenType)) {
            throw new Exception({ message : 'IIB access token and token type must be set.' })
        }
        // keeping a buffer time of 5 mins (300000 ms) for the token expiry
        tokenExpiry = Date.now() + tokenExpiry * 1000
        const refreshTokenCallbackTime = tokenExpiry - 300000
        console.log('ATBase: IIB Token refreshTokenCallbackTime: ',refreshTokenCallbackTime)
        store.dispatch(setTokenInfo({
            _accessToken: accessToken,
            _refreshToken: refreshToken,
            _idToken: idToken,
            _tokenType: tokenType,
            _tokenExpiry: tokenExpiry,
            _refreshTokenCallbackTime: refreshTokenCallbackTime,
        }))
        store.dispatch((setIsLoggedIn(true)))
        const tokenData = JSON.stringify({
            accessToken, refreshToken, idToken, tokenType, tokenExpiry, refreshTokenCallbackTime,
        })
        if (IIBTokenProvider.isLocalStorageTokenAllowed()) {
            window.localStorage.setItem(IIBTokenProvider.IIB_TOKEN_DATA_KEY, tokenData)
            window.localStorage.setItem('ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE', EnvironmentDetail.getDomain())
            return Promise.resolve()
        } else {
            const isCordovaAvailable = (window as any).cordova && _.isFunction((window as any).cordova.exec)
            if (isCordovaAvailable) {
                console.log('ATBase: Executing cordova call to set the token in shared preferences')
                return cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                EnvironmentPluginConstants.ACTION_SET_SHARED_PREFERENCES,
                [IIBTokenProvider.constructJson(IIBTokenProvider.IIB_TOKEN_DATA_KEY, tokenData)])
            }
        }
    }

    public static async getTokenData () {
        return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.FULL)
    }

    public static async getAccessToken () {
        if (getAccessToken(store.getState())) {
            return Promise.resolve(getAccessToken(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.ACCESS_TOKEN)
        }
    }

    public static async getIdToken () {
        if (getIdToken(store.getState())) {
            return Promise.resolve(getIdToken(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.ID_TOKEN)
        }
    }

    public static async getRefreshToken () {
        if (getRefreshToken(store.getState())) {
            return Promise.resolve(getRefreshToken(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.REFRESH_TOKEN)
        }
    }

    public static async getRefreshTokenCallbackTime () {
        if (getRefreshTokenCallbackTime(store.getState())) {
            return Promise.resolve(getRefreshTokenCallbackTime(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.REFRESH_TOKEN_CALLBACK_TIME)
        }
    }

    public static async getTokenExpiry () {
        if (getTokenExpiry(store.getState())) {
            return Promise.resolve(getTokenExpiry(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.TOKEN_EXPIRY)
        }
    }

    public static async getTokenType () {
        if (getTokenType(store.getState())) {
            return Promise.resolve(getTokenType(store.getState()))
        } else {
            return IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.TOKEN_TYPE)
        }
    }

    public static async isTokenSet () {
        const token = await IIBTokenProvider.retrieveTokenData(IIBTokenProvider.IIB_TOKEN_MEMBERS.FULL)
        return !_.isEmpty(token.idToken) && !_.isEmpty(token.tokenType)
    }

    public static async isTokenExpired () {
        const expiry = await IIBTokenProvider.getTokenExpiry()
        if (expiry && expiry > Date.now()) {
            return false
        }
        return true
    }

    public static async retrieveUserDataFromSharedPref () {
        const DEFAULT_USER_DATA: IUserData = {
            userId: null,
            logoutTime: 0,
        }
        try {
            const isCordovaAvailable = (window as any).cordova && _.isFunction((window as any).cordova.exec)
            if (!isCordovaAvailable) {
                console.log('ATBase: Cordova environment not available')
                return DEFAULT_USER_DATA
            }
            console.log('ATBase: Executing cordova call to get userID from shared preferences')
            const userData = await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                EnvironmentPluginConstants.ACTION_GET_SHARED_PREFERENCES_UNENCRYPTED,
                [{ inputKey: IIBTokenProvider.IIB_LAST_LOGGEDIN_USER_ID }])
            if (!userData) {
                console.log('ATBase: No user data found in shared preferences')
                return DEFAULT_USER_DATA
            }
            return JSON.parse(userData)

        } catch (err) {
            console.log('ATBase: Error in fetching userId from SharedPref due to: ', err)
            return DEFAULT_USER_DATA
        }
    }

    private static async retrieveTokenData (key) {
        try {
            let tokenData
            if (IIBTokenProvider.isLocalStorageTokenAllowed()) {
                tokenData = window.localStorage.getItem(IIBTokenProvider.IIB_TOKEN_DATA_KEY)
            } else {
                const isCordovaAvailable = (window as any).cordova && _.isFunction((window as any).cordova.exec)
                if (isCordovaAvailable) {
                    tokenData = await cordovaExec(EnvironmentPluginConstants.ENVIRONMENT_PLUGIN_NAME,
                        EnvironmentPluginConstants.ACTION_GET_SHARED_PREFERENCES,
                        [{ inputKey: IIBTokenProvider.IIB_TOKEN_DATA_KEY }])
                }
            }
            if (key !== IIBTokenProvider.IIB_TOKEN_MEMBERS.FULL) {
                return tokenData !== null && tokenData !== undefined ? JSON.parse(tokenData)[key] : {}
            } else {
                return (tokenData !== null && tokenData !== undefined ? JSON.parse(tokenData) : {})
            }
        } catch (err) {
            console.log('ATBase: Error in fetching IIB token data due to: ',err)
            return {}
        }
    }

    private static isLocalStorageTokenAllowed (): Boolean {
        if (EnvironmentDetail.isAndroid()) {
            return false
        }
        // Temporarily MFCs are allowed to store token in local storage
        // to support Grocery automation effort https://issues.amazon.com/issues/P138731480
        if (EnvironmentDetail.isBrowser() && IIBTokenProvider.ALLOWED_STORES_FOR_LOCALSTORAGE.includes(StoreIdService.getSelectedStoreId() || EnvironmentDetail.getStoreId())) {
            return true
        }
        return IIBTokenProvider.ALLOWED_ENVIRONMENTS_FOR_LOCALSTORAGE.includes(EnvironmentDetail.getDomain())
    }

    private static constructJson (jsonKey, jsonValue) {
        const jsonObj = {}
        jsonObj[jsonKey] = jsonValue
        return jsonObj
    }
}
